import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import TabContent from '../../../components/TabContent';
import Input from '../../../components/input';
import Button from '../../../components/button';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { createNetworkSubnet, listLoadBalancer, deleteListener, createLoadBalancer, createLoadListener, updateLoadListener, listLoadListener } from "../../../hooks/local/userReducer";
import LoadBalancerInstances from './loadBalancerInstances';
import close from "../../../assets/svg-icons/closeBrand.svg";



const CreateListener = ({ isVisible, onClose, loadBalancerId }) => {

  const dispatch = useDispatch();
  const currency = useSelector((state) => state.user.currency);
  const pools = useSelector((state) => state.user.loadBalancer?.pools) || [];
  const projectId = useSelector((state) => state.user.selectedProject)?.id;


  const [selectedTab, setSelectedTab] = useState('Listener Details *');
  const [tabPage, setTabPage] = useState(1);
  const entriesTabPerPage = 5;
  const [isTabValid, setIsTabValid] = useState({
    'Listener Details *': false,
    'Pool Details *': false,
    'Pool Members': false,
    'Monitor Details *': false,
  });

  const tabs = [
    'Listener Details *',
    'Pool Details *',
    'Pool Members',
    'Monitor Details *',
  ];

  const poolOptions = useMemo(() => {
    if (!Array.isArray(pools) || pools.length === 0) {
      return [{ label: 'No pools available', value: '' }];
    }
    return [
      { label: 'Select pool', value: '' },
      ...pools.map((pool) => ({
        label: pool?.name || 'Unnamed Pool',
        value: pool?.id || '',
      })),
    ];
  }, [pools]);

  const getErrorDetails = (errors, touched) => {
    const errorDetails = [];
    const addError = (tab, field, message) => {
      errorDetails.push({ tab, field, message });
    };

    if (touched.createListener && errors.createListener) addError('Listener Details *', 'Create Listener', errors.createListener);
    if (touched.name && errors.name) addError('Listener Details *', 'Name', errors.name);
    if (touched.protocol && errors.protocol) addError('Listener Details *', 'Protocol', errors.protocol);
    if (touched.protocol_port && errors.protocol_port) addError('Listener Details *', 'Port', errors.protocol_port);
    if (touched.default_pool_id && errors.default_pool_id) addError('Listener Details *', 'Default Pool', errors.default_pool_id);
    if (touched.createPool && errors.createPool) addError('Pool Details *', 'Create Pool', errors.createPool);
    if (errors.pool?.name && touched.pool?.name) addError('Pool Details *', 'Pool Name', errors.pool.name);
    if (errors.pool?.session_persistence?.type && touched.pool?.session_persistence?.type) addError('Pool Details *', 'Session Persistence', errors.pool.session_persistence.type);
    if (errors.pool?.session_persistence?.cookie_name && touched.pool?.session_persistence?.cookie_name) addError('Pool Details *', 'Cookie Name', errors.pool.session_persistence.cookie_name);
    if (errors.pool?.lb_algorithm && touched.pool?.lb_algorithm) addError('Pool Details *', 'Algorithm', errors.pool.lb_algorithm);
    if (errors.pool?.members) {
      errors.pool.members.forEach((memberError, index) => {
        if (touched.pool?.members?.[index]?.address && memberError?.address) addError('Pool Members', `Member ${index + 1} IP Address`, memberError.address);
        if (touched.pool?.members?.[index]?.protocol_port && memberError?.protocol_port) addError('Pool Members', `Member ${index + 1} Port`, memberError.protocol_port);
        if (touched.pool?.members?.[index]?.name && memberError?.name) addError('Pool Members', `Member ${index + 1} Name`, memberError.name);
        if (touched.pool?.members?.[index]?.weight && memberError?.weight) addError('Pool Members', `Member ${index + 1} Weight`, memberError.weight);
      });
    }
    if (touched.pool?.createHealthMonitor && errors.pool?.createHealthMonitor) addError('Monitor Details *', 'Create Health Monitor', errors.pool.createHealthMonitor);
    if (errors.pool?.healthmonitor) {
      const monitorErrors = errors.pool.healthmonitor;
      if (touched.pool?.healthmonitor?.name && monitorErrors.name) addError('Monitor Details *', 'Monitor Name', monitorErrors.name);
      if (touched.pool?.healthmonitor?.type && monitorErrors.type) addError('Monitor Details *', 'Monitor Type', monitorErrors.type);
      if (touched.pool?.healthmonitor?.delay && monitorErrors.delay) addError('Monitor Details *', 'Delay', monitorErrors.delay);
      if (touched.pool?.healthmonitor?.max_retries && monitorErrors.max_retries) addError('Monitor Details *', 'Max Retries', monitorErrors.max_retries);
      if (touched.pool?.healthmonitor?.timeout && monitorErrors.timeout) addError('Monitor Details *', 'Timeout', monitorErrors.timeout);
      if (touched.pool?.healthmonitor?.http_method && monitorErrors.http_method) addError('Monitor Details *', 'HTTP Method', monitorErrors.http_method);
      if (touched.pool?.healthmonitor?.expected_codes && monitorErrors.expected_codes) addError('Monitor Details *', 'Expected Codes', monitorErrors.expected_codes);
      if (touched.pool?.healthmonitor?.url_path && monitorErrors.url_path) addError('Monitor Details *', 'URL Path', monitorErrors.url_path);
      if (touched.pool?.healthmonitor?.http_version && monitorErrors.http_version) addError('Monitor Details *', 'HTTP Version', monitorErrors.http_version);
    }

    return errorDetails;
  };

  const createLoadListenerForm = useFormik({
    initialValues: {
      createListener: 'yes',
      name: '',
      description: '',
      protocol: '',
      protocol_port: '',
      timeout_client_data: 50000,
      timeout_tcp_inspect: 0,
      timeout_member_connect: 5000,
      timeout_member_data: 50000,
      connection_limit: -1,
      alpn_protocols: ['http/1.0', 'http/1.1', 'h2'],
      allowed_cidrs: '',
      createPool: 'yes',
      default_pool_id: '',
      pool: {
        name: '',
        alpn_protocols: ['http/1.0', 'http/1.1', 'h2'],
        protocol: '',
        session_persistence: {
          type: '',
          cookie_name: '',
          persistence_timeout: '',
          persistence_granularity: '',
        },
        lb_algorithm: '',
        members: [
          {
            address: '',
            protocol_port: '',
            name: '',
            weight: 1,
            backup: false,
            monitor_address: '',
            monitor_port: '',
          },
        ],
        createHealthMonitor: 'yes',
        healthmonitor: {
          type: '',
          delay: 5,
          max_retries: 3,
          max_retries_down: 3,
          timeout: 5,
          name: '',
          http_method: 'GET',
          expected_codes: '200',
          url_path: '/',
          http_version: '1.0',
        },
      },
    },
    validationSchema: Yup.object({
      createListener: Yup.string().required('Please select whether to create a listener'),
      name: Yup.string().when('createListener', {
        is: 'yes',
        then: (schema) => schema.required('Listener name is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
      protocol: Yup.string().when('createListener', {
        is: 'yes',
        then: (schema) => schema.required('Protocol is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
      protocol_port: Yup.number().when('createListener', {
        is: 'yes',
        then: (schema) =>
          schema
            .required('Port is required')
            .min(1, 'Port must be between 1 and 65535')
            .max(65535, 'Port must be between 1 and 65535'),
        otherwise: (schema) => schema.notRequired(),
      }),
      // default_pool_id: Yup.string().when('createPool', {
      //   is: 'no',
      //   then: (schema) => schema.required('Default pool is required when not creating a pool'),
      //   otherwise: (schema) => schema.notRequired(),
      // }),
      createPool: Yup.string().required('Please select whether to create a pool'),
      pool: Yup.object().when('createPool', {
        is: 'yes',
        then: (schema) =>
          schema.shape({
            name: Yup.string().required('Pool name is required'),
            protocol: Yup.string().required('Protocol is required'),
            session_persistence: Yup.object({
              type: Yup.string().required('Session Persistence is required'),
              cookie_name: Yup.string().when('type', {
                is: (value) => ['APP_COOKIE', 'HTTP_COOKIE'].includes(value),
                then: (schema) => schema.required('Cookie name is required for APP_COOKIE or HTTP_COOKIE'),
                otherwise: (schema) => schema.notRequired(),
              }),
              persistence_timeout: Yup.number().notRequired(),
              persistence_granularity: Yup.string().notRequired(),
            }).required('Session Persistence is required'),
            lb_algorithm: Yup.string().required('Algorithm is required'),
            members: Yup.array().when('createHealthMonitor', {
              is: 'yes',
              then: (schema) =>
                schema.of(
                  Yup.object({
                    address: Yup.string()
                      .matches(
                        /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/,
                        'Invalid IP address format'
                      )
                      .required('IP address is required'),
                    protocol_port: Yup.number()
                      .required('Port is required')
                      .min(1, 'Port must be between 1 and 65535')
                      .max(65535, 'Port must be between 1 and 65535'),
                    name: Yup.string().optional(),
                    weight: Yup.number()
                      .required('Weight is required')
                      .min(1, 'Weight must be between 1 and 256')
                      .max(256, 'Weight must be between 1 and 256'),
                  })
                ),
              otherwise: (schema) => schema.notRequired(),
            }),
            createHealthMonitor: Yup.string().required('Please select whether to create a health monitor'),
            healthmonitor: Yup.object().when('createHealthMonitor', {
              is: 'yes',
              then: (schema) =>
                schema.shape({
                  type: Yup.string().required('Health monitor type is required'),
                  delay: Yup.number()
                    .required('Delay is required')
                    .min(1, 'Delay must be at least 1 second'),
                  max_retries: Yup.number()
                    .required('Max retries is required')
                    .min(1, 'Max retries must be at least 1'),
                  timeout: Yup.number()
                    .required('Timeout is required')
                    .min(1, 'Timeout must be at least 1 second'),
                  name: Yup.string().required('Health monitor name is required'),
                  http_method: Yup.string().when('type', {
                    is: (value) => ['HTTP', 'HTTPS'].includes(value),
                    then: (schema) => schema.required('HTTP Method is required'),
                    otherwise: (schema) => schema.notRequired(),
                  }),
                  expected_codes: Yup.string().when('type', {
                    is: (value) => ['HTTP', 'HTTPS'].includes(value),
                    then: (schema) => schema.required('Expected Codes are required'),
                    otherwise: (schema) => schema.notRequired(),
                  }),
                  url_path: Yup.string().when('type', {
                    is: (value) => ['HTTP', 'HTTPS'].includes(value),
                    then: (schema) => schema.required('URL Path is required'),
                    otherwise: (schema) => schema.notRequired(),
                  }),
                  http_version: Yup.string().when('type', {
                    is: (value) => ['HTTP', 'HTTPS'].includes(value),
                    then: (schema) => schema.required('HTTP Version is required').oneOf(['1.0', '1.1'], 'HTTP Version must be 1.0 or 1.1'),
                    otherwise: (schema) => schema.notRequired(),
                  }),
                }),
              otherwise: (schema) => schema.notRequired(),
            }),
          }),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        await createLoadListenerForm.validateForm();
        const errors = createLoadListenerForm.errors;
        const touched = createLoadListenerForm.touched;

        const errorDetails = getErrorDetails(errors, touched);

        if (errorDetails.length > 0) {
          errorDetails.forEach(({ tab, field, message }) => {
            toast.error(`[${tab}] ${field}: ${message}`, {
              onClick: () => {
                setSelectedTab(tab);
                setTabPage(1);
              },
            });
          });
          setSelectedTab(errorDetails[0].tab);
          setTabPage(1);
          return;
        }

        const createLoadListenerFormData = {
          loadbalancer_id: loadBalancerId,
          projectId,
          name: values.name,
          description: values.description || undefined,
          protocol: values.protocol,
          protocol_port: values.protocol_port,
          timeout_client_data: values.timeout_client_data,
          timeout_tcp_inspect: values.timeout_tcp_inspect,
          timeout_member_connect: values.timeout_member_connect,
          timeout_member_data: values.timeout_member_data,
          connection_limit: values.connection_limit,
          alpn_protocols: values.alpn_protocols,
          allowed_cidrs: values.allowed_cidrs
            ? values.allowed_cidrs.split('\n').filter((cidr) => cidr.trim())
            : undefined,
          default_pool_id: values.createPool === 'no' ? values.default_pool_id : undefined,
          pool: values.createPool === 'yes' ? {
            loadbalancer_id: loadBalancerId,
            name: values.pool.name,
            alpn_protocols: values.pool.alpn_protocols,
            protocol: values.pool.protocol || values.protocol,
            session_persistence:
              values.pool.session_persistence.type && values.pool.session_persistence.type !== 'none'
                ? {
                    type: values.pool.session_persistence.type,
                    cookie_name: values.pool.session_persistence.cookie_name || undefined,
                    persistence_timeout: values.pool.session_persistence.persistence_timeout || undefined,
                    persistence_granularity: values.pool.session_persistence.persistence_granularity || undefined,
                  }
                : undefined,
            lb_algorithm: values.pool.lb_algorithm,
            members: values.pool.members.map((member) => {
              const memberData = {
                address: member.address,
                protocol_port: member.protocol_port,
                weight: member.weight,
                backup: member.backup || undefined,
                monitor_address: member.monitor_address || undefined,
                monitor_port: member.monitor_port || undefined,
              };
              // Only include name if it is not empty
              if (member.name) {
                memberData.name = member.name;
              }
              return memberData;
            }),
            healthmonitor: values.pool.createHealthMonitor === 'yes' ? {
              type: values.pool.healthmonitor.type,
              delay: values.pool.healthmonitor.delay,
              max_retries: values.pool.healthmonitor.max_retries,
              max_retries_down: values.pool.healthmonitor.max_retries_down,
              timeout: values.pool.healthmonitor.timeout,
              name: values.pool.healthmonitor.name,
              http_method: ['HTTP', 'HTTPS'].includes(values.pool.healthmonitor.type)
                ? values.pool.healthmonitor.http_method
                : undefined,
              expected_codes: ['HTTP', 'HTTPS'].includes(values.pool.healthmonitor.type)
                ? values.pool.healthmonitor.expected_codes
                : undefined,
              url_path: ['HTTP', 'HTTPS'].includes(values.pool.healthmonitor.type)
                ? values.pool.healthmonitor.url_path
                : undefined,
              http_version: ['HTTP', 'HTTPS'].includes(values.pool.healthmonitor.type)
                ? values.pool.healthmonitor.http_version
                : undefined,
            } : undefined,
          } : undefined,
        };

        const { payload } = await dispatch(createLoadListener(createLoadListenerFormData));
        if (payload.statuscode === '00') {
          window.location.reload();
        }
      } catch (error) {
        toast.error('An unexpected error occurred during submission.');
        console.error(error);
      }
    },
  });

  const listenerInputs = useMemo(() => [
    {
      type: 'choice',
      element: 'choice',
      name: 'createListener',
      label: 'Create Listener? *',
      choices: [
        { label: 'Yes', value: 'yes' },
        { label: 'No', value: 'no' },
      ],
      value: createLoadListenerForm.values.createListener,
      colSpan: 2,
      onChange: (value) => {
        createLoadListenerForm.setFieldValue('createListener', value);
        if (value === 'yes') {
          createLoadListenerForm.setFieldValue('pool.protocol', createLoadListenerForm.values.protocol);
        }
      },
      onBlur: createLoadListenerForm.handleBlur,
      onError:
        createLoadListenerForm.touched.createListener && createLoadListenerForm.errors.createListener
          ? createLoadListenerForm.errors.createListener
          : null,
    },
    ...(createLoadListenerForm.values.createListener === 'yes' ? [
      {
        type: 'text',
        element: 'input',
        name: 'name',
        label: 'Name *',
        placeholder: 'Enter listener name',
        value: createLoadListenerForm.values.name,
        colSpan: 1,
        onChange: createLoadListenerForm.handleChange,
        onBlur: createLoadListenerForm.handleBlur,
        onError:
          createLoadListenerForm.touched.name && createLoadListenerForm.errors.name
            ? createLoadListenerForm.errors.name
            : null,
      },
      {
        type: 'text',
        element: 'input',
        name: 'description',
        label: 'Description',
        placeholder: 'Enter listener description (optional)',
        value: createLoadListenerForm.values.description,
        colSpan: 1,
        onChange: createLoadListenerForm.handleChange,
        onBlur: createLoadListenerForm.handleBlur,
        onError:
          createLoadListenerForm.touched.description && createLoadListenerForm.errors.description
            ? createLoadListenerForm.errors.description
            : null,
      },
      {
        type: 'text',
        element: 'select',
        name: 'protocol',
        label: 'Protocol *',
        placeholder: 'Select protocol',
        options: [
          { label: 'Select protocol', value: '' },
          { label: 'HTTP', value: 'HTTP' },
          { label: 'HTTPS', value: 'HTTPS' },
          { label: 'PROXY', value: 'PROXY' },
          { label: 'PROXYV2', value: 'PROXYV2' },
          { label: 'SCTP', value: 'SCTP' },
          { label: 'TCP', value: 'TCP' },
          { label: 'UDP', value: 'UDP' },
        ],
        value: createLoadListenerForm.values.protocol,
        colSpan: 1,
        onChange: (e) => {
          createLoadListenerForm.handleChange(e);
          createLoadListenerForm.setFieldValue('pool.protocol', e.target.value);
        },
        onBlur: createLoadListenerForm.handleBlur,
        onError:
          createLoadListenerForm.touched.protocol && createLoadListenerForm.errors.protocol
            ? createLoadListenerForm.errors.protocol
            : null,
      },
      {
        type: 'number',
        element: 'input',
        name: 'protocol_port',
        label: 'Port *',
        placeholder: '443',
        value: createLoadListenerForm.values.protocol_port,
        colSpan: 1,
        onChange: createLoadListenerForm.handleChange,
        onBlur: createLoadListenerForm.handleBlur,
        onError:
          createLoadListenerForm.touched.protocol_port && createLoadListenerForm.errors.protocol_port
            ? createLoadListenerForm.errors.protocol_port
            : null,
      },
      {
        type: 'number',
        element: 'input',
        name: 'timeout_client_data',
        label: 'Client Data Timeout',
        placeholder: '5000',
        value: createLoadListenerForm.values.timeout_client_data,
        colSpan: 1,
        onChange: createLoadListenerForm.handleChange,
        onBlur: createLoadListenerForm.handleBlur,
        onError:
          createLoadListenerForm.touched.timeout_client_data && createLoadListenerForm.errors.timeout_client_data
            ? createLoadListenerForm.errors.timeout_client_data
            : null,
      },
      {
        type: 'number',
        element: 'input',
        name: 'timeout_tcp_inspect',
        label: 'TCP Inspect Timeout',
        placeholder: '0',
        value: createLoadListenerForm.values.timeout_tcp_inspect,
        colSpan: 1,
        onChange: createLoadListenerForm.handleChange,
        onBlur: createLoadListenerForm.handleBlur,
        onError:
          createLoadListenerForm.touched.timeout_tcp_inspect && createLoadListenerForm.errors.timeout_tcp_inspect
            ? createLoadListenerForm.errors.timeout_tcp_inspect
            : null,
      },
      {
        type: 'number',
        element: 'input',
        name: 'timeout_member_connect',
        label: 'Member Connect Timeout',
        placeholder: '5000',
        value: createLoadListenerForm.values.timeout_member_connect,
        colSpan: 1,
        onChange: createLoadListenerForm.handleChange,
        onBlur: createLoadListenerForm.handleBlur,
        onError:
          createLoadListenerForm.touched.timeout_member_connect && createLoadListenerForm.errors.timeout_member_connect
            ? createLoadListenerForm.errors.timeout_member_connect
            : null,
      },
      {
        type: 'number',
        element: 'input',
        name: 'timeout_member_data',
        label: 'Member Data Timeout',
        placeholder: '50000',
        value: createLoadListenerForm.values.timeout_member_data,
        colSpan: 1,
        onChange: createLoadListenerForm.handleChange,
        onBlur: createLoadListenerForm.handleBlur,
        onError:
          createLoadListenerForm.touched.timeout_member_data && createLoadListenerForm.errors.timeout_member_data
            ? createLoadListenerForm.errors.timeout_member_data
            : null,
      },
      {
        type: 'number',
        element: 'input',
        name: 'connection_limit',
        label: 'Connection Limit',
        placeholder: '-1',
        value: createLoadListenerForm.values.connection_limit,
        colSpan: 1,
        onChange: createLoadListenerForm.handleChange,
        onBlur: createLoadListenerForm.handleBlur,
        onError:
          createLoadListenerForm.touched.connection_limit && createLoadListenerForm.errors.connection_limit
            ? createLoadListenerForm.errors.connection_limit
            : null,
      },
      {
        type: 'text',
        element: 'textarea',
        name: 'allowed_cidrs',
        label: 'Allowed CIDRs',
        placeholder: 'Enter allowed CIDRs (one per line, optional)',
        value: createLoadListenerForm.values.allowed_cidrs,
        colSpan: 2,
        onChange: createLoadListenerForm.handleChange,
        onBlur: createLoadListenerForm.handleBlur,
        onError:
          createLoadListenerForm.touched.allowed_cidrs && createLoadListenerForm.errors.allowed_cidrs
            ? createLoadListenerForm.errors.allowed_cidrs
            : null,
      },
    ] : []),
    ...(createLoadListenerForm.values.createPool === 'no' ? [
      {
        type: 'text',
        element: 'select',
        name: 'default_pool_id',
        label: 'Default Pool *',
        placeholder: 'Select default pool',
        options: poolOptions,
        value: createLoadListenerForm.values.default_pool_id,
        colSpan: 2,
        onChange: createLoadListenerForm.handleChange,
        onBlur: createLoadListenerForm.handleBlur,
        onError:
          createLoadListenerForm.touched.default_pool_id && createLoadListenerForm.errors.default_pool_id
            ? createLoadListenerForm.errors.default_pool_id
            : null,
      },
    ] : []),
  ], [createLoadListenerForm.values.createListener, createLoadListenerForm.values.createPool, createLoadListenerForm.values, createLoadListenerForm.touched, createLoadListenerForm.errors, createLoadListenerForm.handleChange, createLoadListenerForm.handleBlur, createLoadListenerForm.setFieldValue, poolOptions]);

  const poolInputs = useMemo(() => [
    {
      type: 'choice',
      element: 'choice',
      name: 'createPool',
      label: 'Create Pool? *',
      choices: [
        { label: 'Yes', value: 'yes' },
        { label: 'No', value: 'no' },
      ],
      value: createLoadListenerForm.values.createPool,
      colSpan: 2,
      onChange: (value) => createLoadListenerForm.setFieldValue('createPool', value),
      onBlur: createLoadListenerForm.handleBlur,
      onError:
        createLoadListenerForm.touched.createPool && createLoadListenerForm.errors.createPool
          ? createLoadListenerForm.errors.createPool
          : null,
    },
    ...(createLoadListenerForm.values.createPool === 'yes' ? [
      {
        type: 'text',
        element: 'input',
        name: 'pool.name',
        label: 'Name *',
        placeholder: 'Enter pool name',
        value: createLoadListenerForm.values.pool.name,
        colSpan: 1,
        onChange: createLoadListenerForm.handleChange,
        onBlur: createLoadListenerForm.handleBlur,
        onError:
          createLoadListenerForm.touched.pool?.name && createLoadListenerForm.errors.pool?.name
            ? createLoadListenerForm.errors.pool.name
            : null,
      },
      {
        type: 'text',
        element: 'select',
        name: 'pool.session_persistence.type',
        label: 'Session Persistence *',
        placeholder: 'Select session persistence',
        options: [
          { label: 'Select session persistence', value: '' },
          { label: 'None', value: 'none' },
          { label: 'SOURCE IP', value: 'SOURCE_IP' },
          { label: 'HTTP_COOKIE', value: 'HTTP_COOKIE' },
          { label: 'APP_COOKIE', value: 'APP_COOKIE' },
        ],
        value: createLoadListenerForm.values.pool.session_persistence.type,
        colSpan: 1,
        onChange: createLoadListenerForm.handleChange,
        onBlur: createLoadListenerForm.handleBlur,
        onError:
          createLoadListenerForm.touched.pool?.session_persistence?.type &&
          createLoadListenerForm.errors.pool?.session_persistence?.type
            ? createLoadListenerForm.errors.pool.session_persistence.type
            : null,
      },
      ...(createLoadListenerForm.values.pool.session_persistence.type === 'APP_COOKIE' ||
        createLoadListenerForm.values.pool.session_persistence.type === 'HTTP_COOKIE'
        ? [
            {
              type: 'text',
              element: 'input',
              name: 'pool.session_persistence.cookie_name',
              label: 'Cookie Name *',
              placeholder: 'Enter cookie name',
              value: createLoadListenerForm.values.pool.session_persistence.cookie_name,
              colSpan: 1,
              onChange: createLoadListenerForm.handleChange,
              onBlur: createLoadListenerForm.handleBlur,
              onError:
                createLoadListenerForm.touched.pool?.session_persistence?.cookie_name &&
                createLoadListenerForm.errors.pool?.session_persistence?.cookie_name
                  ? createLoadListenerForm.errors.pool.session_persistence.cookie_name
                  : null,
            },
          ]
        : []),
      {
        type: 'number',
        element: 'input',
        name: 'pool.session_persistence.persistence_timeout',
        label: 'Persistence Timeout',
        placeholder: 'Enter persistence timeout (optional)',
        value: createLoadListenerForm.values.pool.session_persistence.persistence_timeout,
        colSpan: 1,
        onChange: createLoadListenerForm.handleChange,
        onBlur: createLoadListenerForm.handleBlur,
        onError:
          createLoadListenerForm.touched.pool?.session_persistence?.persistence_timeout &&
          createLoadListenerForm.errors.pool?.session_persistence?.persistence_timeout
            ? createLoadListenerForm.errors.pool.session_persistence.persistence_timeout
            : null,
      },
      {
        type: 'text',
        element: 'input',
        name: 'pool.session_persistence.persistence_granularity',
        label: 'Persistence Granularity',
        placeholder: 'Enter persistence granularity (optional)',
        value: createLoadListenerForm.values.pool.session_persistence.persistence_granularity,
        colSpan: 1,
        onChange: createLoadListenerForm.handleChange,
        onBlur: createLoadListenerForm.handleBlur,
        onError:
          createLoadListenerForm.touched.pool?.session_persistence?.persistence_granularity &&
          createLoadListenerForm.errors.pool?.session_persistence?.persistence_granularity
            ? createLoadListenerForm.errors.pool.session_persistence.persistence_granularity
            : null,
      },
      {
        type: 'text',
        element: 'select',
        name: 'pool.lb_algorithm',
        label: 'Algorithm *',
        placeholder: 'Select algorithm',
        options: [
          { label: 'Select algorithm', value: '' },
          { label: 'LEAST CONNECTIONS', value: 'LEAST_CONNECTIONS' },
          { label: 'ROUND ROBIN', value: 'ROUND_ROBIN' },
          { label: 'SOURCE IP', value: 'SOURCE_IP' },
        ],
        value: createLoadListenerForm.values.pool.lb_algorithm,
        colSpan: 1,
        onChange: createLoadListenerForm.handleChange,
        onBlur: createLoadListenerForm.handleBlur,
        onError:
          createLoadListenerForm.touched.pool?.lb_algorithm &&
          createLoadListenerForm.errors.pool?.lb_algorithm
            ? createLoadListenerForm.errors.pool.lb_algorithm
            : null,
      },
    ] : []),
  ], [createLoadListenerForm.values.createPool, createLoadListenerForm.values.pool, createLoadListenerForm.touched, createLoadListenerForm.errors, createLoadListenerForm.handleChange, createLoadListenerForm.handleBlur, createLoadListenerForm.setFieldValue]);

  const monitorInputs = useMemo(() => [
    {
      type: 'choice',
      element: 'choice',
      name: 'pool.createHealthMonitor',
      label: 'Create Health Monitor? *',
      choices: [
        { label: 'Yes', value: 'yes' },
        { label: 'No', value: 'no' },
      ],
      value: createLoadListenerForm.values.pool.createHealthMonitor,
      colSpan: 2,
      onChange: (value) => createLoadListenerForm.setFieldValue('pool.createHealthMonitor', value),
      onBlur: createLoadListenerForm.handleBlur,
      onError:
        createLoadListenerForm.touched.pool?.createHealthMonitor &&
        createLoadListenerForm.errors.pool?.createHealthMonitor
          ? createLoadListenerForm.errors.pool.createHealthMonitor
          : null,
    },
    ...(createLoadListenerForm.values.createPool === 'yes' && createLoadListenerForm.values.pool.createHealthMonitor === 'yes' ? [
      {
        type: 'text',
        element: 'input',
        name: 'pool.healthmonitor.name',
        label: 'Name *',
        placeholder: 'Enter health monitor name',
        value: createLoadListenerForm.values.pool.healthmonitor.name,
        colSpan: 1,
        onChange: createLoadListenerForm.handleChange,
        onBlur: createLoadListenerForm.handleBlur,
        onError:
          createLoadListenerForm.touched.pool?.healthmonitor?.name &&
          createLoadListenerForm.errors.pool?.healthmonitor?.name
            ? createLoadListenerForm.errors.pool.healthmonitor.name
            : null,
      },
      {
        type: 'text',
        element: 'select',
        name: 'pool.healthmonitor.type',
        label: 'Type *',
        placeholder: 'Select type',
        options: [
          { label: 'Select type', value: '' },
          { label: 'HTTP', value: 'HTTP' },
          { label: 'HTTPS', value: 'HTTPS' },
          { label: 'PING', value: 'PING' },
          { label: 'SCTP', value: 'SCTP' },
          { label: 'TCP', value: 'TCP' },
          { label: 'TLS-HELLO', value: 'TLS-HELLO' },
          { label: 'UDP-CONNECT', value: 'UDP-CONNECT' },
        ],
        value: createLoadListenerForm.values.pool.healthmonitor.type,
        colSpan: 1,
        onChange: createLoadListenerForm.handleChange,
        onBlur: createLoadListenerForm.handleBlur,
        onError:
          createLoadListenerForm.touched.pool?.healthmonitor?.type &&
          createLoadListenerForm.errors.pool?.healthmonitor?.type
            ? createLoadListenerForm.errors.pool.healthmonitor.type
            : null,
      },
      {
        type: 'number',
        element: 'input',
        name: 'pool.healthmonitor.delay',
        label: 'Delay (sec) *',
        placeholder: '3',
        value: createLoadListenerForm.values.pool.healthmonitor.delay,
        colSpan: 1,
        onChange: createLoadListenerForm.handleChange,
        onBlur: createLoadListenerForm.handleBlur,
        onError:
          createLoadListenerForm.touched.pool?.healthmonitor?.delay &&
          createLoadListenerForm.errors.pool?.healthmonitor?.delay
            ? createLoadListenerForm.errors.pool.healthmonitor.delay
            : null,
      },
      {
        type: 'number',
        element: 'input',
        name: 'pool.healthmonitor.max_retries',
        label: 'Max Retries *',
        placeholder: '2',
        value: createLoadListenerForm.values.pool.healthmonitor.max_retries,
        colSpan: 1,
        onChange: createLoadListenerForm.handleChange,
        onBlur: createLoadListenerForm.handleBlur,
        onError:
          createLoadListenerForm.touched.pool?.healthmonitor?.max_retries &&
          createLoadListenerForm.errors.pool?.healthmonitor?.max_retries
            ? createLoadListenerForm.errors.pool.healthmonitor.max_retries
            : null,
      },
      {
        type: 'number',
        element: 'input',
        name: 'pool.healthmonitor.max_retries_down',
        label: 'Max Retries Down',
        placeholder: '3',
        value: createLoadListenerForm.values.pool.healthmonitor?.max_retries_down,
        colSpan: 1,
        onChange: createLoadListenerForm.handleChange,
        onBlur: createLoadListenerForm.handleBlur,
        onError:
          createLoadListenerForm.touched.pool?.healthmonitor?.max_retries_down &&
          createLoadListenerForm.errors.pool?.healthmonitor?.max_retries_down
            ? createLoadListenerForm.errors.pool.healthmonitor.max_retries_down
            : null,
      },
      {
        type: 'number',
        element: 'input',
        name: 'pool.healthmonitor.timeout',
        label: 'Timeout (sec) *',
        placeholder: '1',
        value: createLoadListenerForm.values.pool.healthmonitor.timeout,
        colSpan: 1,
        onChange: createLoadListenerForm.handleChange,
        onBlur: createLoadListenerForm.handleBlur,
        onError:
          createLoadListenerForm.touched.pool?.healthmonitor?.timeout &&
          createLoadListenerForm.errors.pool?.healthmonitor?.timeout
            ? createLoadListenerForm.errors.pool.healthmonitor.timeout
            : null,
      },
      ...(createLoadListenerForm.values.pool.healthmonitor.type === 'HTTP' || 
          createLoadListenerForm.values.pool.healthmonitor.type === 'HTTPS' ? [
        {
          type: 'text',
          element: 'select',
          name: 'pool.healthmonitor.http_method',
          label: 'HTTP Method *',
          placeholder: 'Select HTTP method',
          options: [
            { label: 'CONNECT', value: 'CONNECT' },
            { label: 'DELETE', value: 'DELETE' },
            { label: 'GET', value: 'GET' },
            { label: 'HEAD', value: 'HEAD' },
            { label: 'OPTIONS', value: 'OPTIONS' },
            { label: 'PATCH', value: 'PATCH' },
            { label: 'POST', value: 'POST' },
            { label: 'PUT', value: 'PUT' },
            { label: 'TRACE', value: 'TRACE' },
          ],
          value: createLoadListenerForm.values.pool.healthmonitor.http_method,
          colSpan: 1,
          onChange: createLoadListenerForm.handleChange,
          onBlur: createLoadListenerForm.handleBlur,
          onError:
            createLoadListenerForm.touched.pool?.healthmonitor?.http_method &&
            createLoadListenerForm.errors.pool?.healthmonitor?.http_method
              ? createLoadListenerForm.errors.pool.healthmonitor.http_method
              : null,
        },
        {
          type: 'text',
          element: 'input',
          name: 'pool.healthmonitor.expected_codes',
          label: 'Expected Codes *',
          placeholder: 'e.g., 200, 200-204',
          value: createLoadListenerForm.values.pool.healthmonitor.expected_codes,
          colSpan: 1,
          onChange: createLoadListenerForm.handleChange,
          onBlur: createLoadListenerForm.handleBlur,
          onError:
            createLoadListenerForm.touched.pool?.healthmonitor?.expected_codes &&
            createLoadListenerForm.errors.pool?.healthmonitor?.expected_codes
              ? createLoadListenerForm.errors.pool.healthmonitor.expected_codes
              : null,
        },
        {
          type: 'text',
          element: 'input',
          name: 'pool.healthmonitor.url_path',
          label: 'URL Path *',
          placeholder: 'e.g., /health',
          value: createLoadListenerForm.values.pool.healthmonitor.url_path,
          colSpan: 1,
          onChange: createLoadListenerForm.handleChange,
          onBlur: createLoadListenerForm.handleBlur,
          onError:
            createLoadListenerForm.touched.pool?.healthmonitor?.url_path &&
            createLoadListenerForm.errors.pool?.healthmonitor?.url_path
              ? createLoadListenerForm.errors.pool.healthmonitor.url_path
              : null,
        },
        {
          type: 'text',
          element: 'select',
          name: 'pool.healthmonitor.http_version',
          label: 'HTTP Version *',
          placeholder: 'Select HTTP version',
          options: [
            { label: '1.0', value: '1.0' },
            { label: '1.1', value: '1.1' },
          ],
          value: createLoadListenerForm.values.pool.healthmonitor.http_version,
          colSpan: 1,
          onChange: createLoadListenerForm.handleChange,
          onBlur: createLoadListenerForm.handleBlur,
          onError:
            createLoadListenerForm.touched.pool?.healthmonitor?.http_version &&
            createLoadListenerForm.errors.pool?.healthmonitor?.http_version
              ? createLoadListenerForm.errors.pool.healthmonitor.http_version
              : null,
        },
      ] : []),
    ] : []),
  ], [createLoadListenerForm.values.createPool, createLoadListenerForm.values.pool, createLoadListenerForm.touched, createLoadListenerForm.errors, createLoadListenerForm.handleChange, createLoadListenerForm.handleBlur, createLoadListenerForm.setFieldValue]);

  const tabConfigs = {
    'Listener Details *': {
      title: 'Provide the details for the listener',
      inputs: listenerInputs,
    },
    'Pool Details *': {
      title: 'Provide the details for the pool',
      inputs: poolInputs,
    },
    'Pool Members': {
      title: 'Provide the details for pool members',
      inputs: [],
    },
    'Monitor Details *': {
      title: 'Provide the details for the monitor',
      inputs: monitorInputs,
    },
  };

  const addMember = () => {
    const newMembers = [
      ...createLoadListenerForm.values.pool.members,
      {
        address: '',
        protocol_port: '',
        name: '',
        weight: '',
        backup: false,
        monitor_address: '',
        monitor_port: '',
      },
    ];
    createLoadListenerForm.setFieldValue('pool.members', newMembers);
  };

  const removeMember = (index) => {
    const newMembers = createLoadListenerForm.values.pool.members.filter(
      (_, i) => i !== index
    );
    createLoadListenerForm.setFieldValue('pool.members', newMembers);
  };

  useEffect(() => {
    const validateTab = async () => {
      const errors = await createLoadListenerForm.validateForm();
      const values = createLoadListenerForm.values;
      const touched = createLoadListenerForm.touched;

      const tabValidation = {
        'Listener Details *':
          !errors.createListener &&
          (values.createListener === 'no' ||
            (values.name &&
             values.protocol &&
             values.protocol_port &&
             !errors.name &&
             !errors.protocol &&
             !errors.protocol_port &&
             (values.createPool === 'yes' || (values.createPool === 'no' && values.default_pool_id && !errors.default_pool_id)))),
        'Pool Details *':
          !errors.createPool &&
          (values.createPool === 'no' ||
            (values.pool.name &&
             values.pool.session_persistence.type &&
             values.pool.lb_algorithm &&
             values.pool.protocol &&
             !errors.pool?.name &&
             !errors.pool?.session_persistence?.type &&
             !errors.pool?.lb_algorithm &&
             !errors.pool?.protocol)),
        'Pool Members':
          values.createPool === 'no' ||
          values.pool.createHealthMonitor === 'no' ||
          values.pool.members.every(
            (member) =>
              member.address &&
              member.protocol_port &&
              member.name &&
              member.weight &&
              !errors.pool?.members?.find((m) => m?.address === member?.address)
          ),
        'Monitor Details *':
          values.createPool === 'no' ||
          !errors.pool?.createHealthMonitor &&
          (values.pool.createHealthMonitor === 'no' ||
            (values.pool.healthmonitor.name &&
             values.pool.healthmonitor.type &&
             values.pool.healthmonitor.delay &&
             values.pool.healthmonitor.max_retries &&
             values.pool.healthmonitor.timeout &&
             (!['HTTP', 'HTTPS'].includes(values.pool.healthmonitor.type) ||
               (values.pool.healthmonitor.http_method &&
                values.pool.healthmonitor.expected_codes &&
                values.pool.healthmonitor.url_path &&
                values.pool.healthmonitor.http_version)) &&
             !errors.pool?.healthmonitor?.name &&
             !errors.pool?.healthmonitor?.type &&
             !errors.pool?.healthmonitor?.delay &&
             !errors.pool?.healthmonitor?.max_retries &&
             !errors.pool?.healthmonitor?.timeout &&
             !errors.pool?.healthmonitor?.http_method &&
             !errors.pool?.healthmonitor?.expected_codes &&
             !errors.pool?.healthmonitor?.url_path &&
             !errors.pool?.healthmonitor?.http_version)),
      };

      setIsTabValid(tabValidation);
    };
    validateTab();
  }, [createLoadListenerForm.values, createLoadListenerForm.errors, createLoadListenerForm.touched]);

  const navigateToPrevious = async () => {
    await createLoadListenerForm.validateForm();
    const errors = createLoadListenerForm.errors;
    const touched = createLoadListenerForm.touched;
    const errorDetails = getErrorDetails(errors, touched).filter((error) => error.tab === selectedTab);

    if (errorDetails.length > 0) {
      errorDetails.forEach(({ field, message }) => {
        toast.error(`[${selectedTab}] ${field}: ${message}`);
      });
      return;
    }

    const tabIndex = tabs.indexOf(selectedTab);
    if (selectedTab === 'Pool Members' && tabPage > 1) {
      setTabPage(tabPage - 1);
    } else if (tabIndex > 0) {
      setSelectedTab(tabs[tabIndex - 1]);
      setTabPage(1);
    }
  };

  const navigateToNext = async () => {
    await createLoadListenerForm.validateForm();
    const errors = createLoadListenerForm.errors;
    const touched = createLoadListenerForm.touched;
    const errorDetails = getErrorDetails(errors, touched).filter((error) => error.tab === selectedTab);

    if (errorDetails.length > 0) {
      errorDetails.forEach(({ field, message }) => {
        toast.error(`[${selectedTab}] ${field}: ${message}`);
      });
      return;
    }

    const tabIndex = tabs.indexOf(selectedTab);
    if (selectedTab === 'Pool Members' && tabPage < Math.ceil(createLoadListenerForm.values.pool.members.length / entriesTabPerPage)) {
      setTabPage(tabPage + 1);
    } else if (tabIndex < tabs.length - 1) {
      setSelectedTab(tabs[tabIndex + 1]);
      setTabPage(1);
    }
  };

  const lastEntryIndex = tabPage * entriesTabPerPage;
  const firstEntryIndex = lastEntryIndex - entriesTabPerPage;
  const visibleEntries = createLoadListenerForm.values.pool.members.slice(
    firstEntryIndex,
    lastEntryIndex
  );
  const totalTabPages = Math.ceil(
    createLoadListenerForm.values.pool.members.length / entriesTabPerPage
  );

  if (!isVisible) return null;

  return (
    <div className="fixed top-0 h-screen w-full left-0 z-50 flex items-center justify-center p-4 bg-[rgba(0,0,0,0.5)]">
      <div className="bg-white dark:bg-[#141D26] rounded-md p-4 md:p-10 w-full md:w-[80%] max-h-[95%] overflow-y-scroll shadow-xl shadow-[rgba(0,0,0,0.5)]">
        <div className="flex items-center justify-between mb-4">
          <p className="text-primaryBlue">Create Listener</p>
          <span onClick={onClose} className="cursor-pointer">
            <img alt="Close" src={close} className="h-5" />
          </span>
        </div>
        <div className="w-full flex flex-col lg:flex-row justify-center lg:justify-start items-center lg:items-start gap-4">
          <div className="flex flex-wrap lg:flex-col justify-center lg:justify-start items-center lg:items-start gap-4 w-full lg:w-[30%] border-b lg:border-r border-gray-200 pb-4 lg:pb-0">
            {tabs.map((tab, index) => (
              <button
                key={index}
                className={`w-fit lg:w-[90%] ${
                  selectedTab === tab ? 'bg-primaryBlue text-white' : 'bg-transparent'
                } py-4 px-6 rounded-md ${
                  selectedTab === tab ? 'text-white' : 'text-primaryBlue'
                } text-sm font-medium`}
                onClick={() => {
                  setSelectedTab(tab);
                  setTabPage(1);
                }}
              >
                {tab}
              </button>
            ))}
          </div>
          <div className="w-full lg:w-[70%] flex flex-col justify-start items-start gap-2 py-4 px-4 h-[400px] overflow-y-auto">
            <form onSubmit={createLoadListenerForm.handleSubmit} className="w-full">
              {selectedTab !== 'Pool Members' ? (
                <>
                  {tabConfigs[selectedTab] && (
                    <TabContent
                      title={tabConfigs[selectedTab].title}
                      inputs={tabConfigs[selectedTab].inputs}
                    />
                  )}
                </>
              ) : (
                <div className="w-full">
                  <h3 className="text-gray-800 text-xl font-semibold mb-10">
                    {tabConfigs[selectedTab]?.title}
                  </h3>
                  <div className="w-full">
                    <h3 className="text-gray-700 text-lg font-semibold mb-4">
                      Pool Members
                    </h3>
                    {createLoadListenerForm.values.createPool === 'yes' && createLoadListenerForm.values.pool.createHealthMonitor === 'yes' && (
                      <div className="mb-6 p-4 bg-blue-100 dark:bg-blue-900 rounded-md">
                        <p className="text-gray-800 dark:text-gray-200 text-sm">
                          <span className="font-semibold">Pricing:</span> Each pool member/node costs{' '}
                          {currency === 'NGN' ? (
                            <>
                              <span className="font-bold">₦6,000</span>
                            </>
                          ) : (
                            <>
                              <span className="font-bold">$4</span> per month.
                            </>
                          )}
                        </p>
                        <p className="text-gray-800 dark:text-gray-200 text-sm mt-2">
                          You have added{' '}
                          <span className="font-bold">{createLoadListenerForm.values.pool.members.length}</span> member(s).
                          Estimated monthly cost:{' '}
                          {currency === 'NGN' ? (
                            <>
                              <span className="font-bold">₦{createLoadListenerForm.values.pool.members.length * 17500}</span>
                            </>
                          ) : (
                            <>
                              <span className="font-bold">${createLoadListenerForm.values.pool.members.length * 11}</span>.
                            </>
                          )}
                        </p>
                      </div>
                    )}
                    {createLoadListenerForm.values.createPool === 'yes' && createLoadListenerForm.values.pool.createHealthMonitor === 'yes' ? (
                      visibleEntries.map((member, index) => (
                        <div
                          key={index + firstEntryIndex}
                          className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 border-b pb-4"
                        >
                          <Input
                            type="text"
                            label="IP Address *"
                            name={`pool.members[${index + firstEntryIndex}].address`}
                            placeholder="e.g., 192.0.2.51"
                            value={member.address}
                            onChange={createLoadListenerForm.handleChange}
                            onBlur={createLoadListenerForm.handleBlur}
                            onError={
                              createLoadListenerForm.touched.pool?.members?.[index + firstEntryIndex]?.address &&
                              createLoadListenerForm.errors.pool?.members?.[index + firstEntryIndex]?.address
                                ? createLoadListenerForm.errors.pool.members[index + firstEntryIndex].address
                                : null
                            }
                          />
                          <Input
                            type="number"
                            label="Port *"
                            name={`pool.members[${index + firstEntryIndex}].protocol_port`}
                            placeholder="e.g., 80"
                            value={member.protocol_port}
                            onChange={createLoadListenerForm.handleChange}
                            onBlur={createLoadListenerForm.handleBlur}
                            onError={
                              createLoadListenerForm.touched.pool?.members?.[index + firstEntryIndex]?.protocol_port &&
                              createLoadListenerForm.errors.pool?.members?.[index + firstEntryIndex]?.protocol_port
                                ? createLoadListenerForm.errors.pool.members[index + firstEntryIndex].protocol_port
                                : null
                            }
                          />
                          <Input
                            type="text"
                            label="Name"
                            name={`pool.members[${index + firstEntryIndex}].name`}
                            placeholder="e.g., Member 1"
                            value={member.name}
                            onChange={createLoadListenerForm.handleChange}
                            onBlur={createLoadListenerForm.handleBlur}
                            onError={
                              createLoadListenerForm.touched.pool?.members?.[index + firstEntryIndex]?.name &&
                              createLoadListenerForm.errors.pool?.members?.[index + firstEntryIndex]?.name
                                ? createLoadListenerForm.errors.pool.members[index + firstEntryIndex].name
                                : null
                            }
                          />
                          <Input
                            type="number"
                            label="Weight *"
                            name={`pool.members[${index + firstEntryIndex}].weight`}
                            placeholder="1"
                            value={member.weight}
                            onChange={createLoadListenerForm.handleChange}
                            onBlur={createLoadListenerForm.handleBlur}
                            onError={
                              createLoadListenerForm.touched.pool?.members?.[index + firstEntryIndex]?.weight &&
                              createLoadListenerForm.errors.pool?.members?.[index + firstEntryIndex]?.weight
                                ? createLoadListenerForm.errors.pool.members[index + firstEntryIndex].weight
                                : null
                            }
                          />
                          <button
                            type="button"
                            className="text-red-500 text-sm"
                            onClick={() => removeMember(index + firstEntryIndex)}
                            disabled={createLoadListenerForm.values.pool.members.length === 1}
                          >
                            Remove
                          </button>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500">
                        Pool members are not required because pool or health monitor creation is disabled.
                      </p>
                    )}
                    {createLoadListenerForm.values.createPool === 'yes' && createLoadListenerForm.values.pool.createHealthMonitor === 'yes' && (
                      <>
                        <button
                          type="button"
                          className="py-2 px-4 rounded-md text-sm border border-primaryBlue text-primaryBlue cursor-pointer"
                          onClick={addMember}
                        >
                          Add Member
                        </button>
                        <div className="flex justify-between items-center mt-4">
                          <p className="text-gray-400 text-sm">
                            Showing{' '}
                            <span className="font-medium text-black dark:text-white">
                              {firstEntryIndex + 1}
                            </span>{' '}
                            to{' '}
                            <span className="font-medium text-black dark:text-white">
                              {Math.min(
                                lastEntryIndex,
                                createLoadListenerForm.values.pool.members.length
                              )}
                            </span>{' '}
                            of{' '}
                            <span className="font-medium text-black dark:text-white">
                              {createLoadListenerForm.values.pool.members.length}
                            </span>{' '}
                            members
                          </p>
                          <div className="flex gap-3">
                            <button
                              className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${
                                tabPage === 1 ? 'opacity-50' : 'opacity-100'
                              }`}
                              onClick={navigateToPrevious}
                              disabled={tabPage === 1}
                              type="button"
                            >
                              Previous
                            </button>
                            <button
                              className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${
                                tabPage === totalTabPages ? 'opacity-50' : 'opacity-100'
                              }`}
                              onClick={navigateToNext}
                              disabled={tabPage === totalTabPages}
                              type="button"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      </>
                    )}

                      <LoadBalancerInstances/>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
        <div className="w-full flex justify-between items-center md:flex-nowrap p-6 border-t border-gray-200">
          <button
            type="button"
            className="px-4 py-4 text-xs border border-primaryBlue rounded-sm text-primaryBlue cursor-pointer text-center"
            onClick={onClose}
          >
            Cancel
          </button>
          <div className="w-full flex justify-between md:justify-end items-end md:items-center gap-2">
            <button
              type="button"
              className={`px-4 py-4 text-xs border border-primaryBlue rounded-sm text-primaryBlue cursor-pointer text-center ${
                tabs.indexOf(selectedTab) === 0 && tabPage === 1 ? 'opacity-50' : 'opacity-100'
              }`}
              onClick={navigateToPrevious}
              disabled={tabs.indexOf(selectedTab) === 0 && tabPage === 1}
            >
              {'<'} Back
            </button>
            {selectedTab !== tabs[tabs.length - 1] ? (
              <button
                type="button"
                className={`px-4 py-4 text-xs border border-primaryBlue rounded-sm text-primaryBlue cursor-pointer text-center ${
                  tabs.indexOf(selectedTab) === tabs.length - 1 ? 'opacity-50' : 'opacity-100'
                }`}
                onClick={navigateToNext}
                disabled={tabs.indexOf(selectedTab) === tabs.length - 1}
              >
                Next {'>'}
              </button>
            ) : (
              <Button
                buttonText="Create Listener"
                buttonRole="submit"
                type="submit"
                handleClick={createLoadListenerForm.handleSubmit}
                // disabled={!isTabValid['Listener Details *']}
              />
            )}
          </div>
        </div>
        <ToastContainer />
      </div>
    </div>
  );
};

export default CreateListener;

